import {
  Achievement,
  AIRecommendation,
  EditDraftRequest,
  Improvement,
} from "../types";

type DraftPatchInput = {
  baselineAchievements: Achievement[];
  baselineImprovements: Improvement[];
  baselineRecommendations: AIRecommendation[];
  currentAchievements: Achievement[];
  currentImprovements: Improvement[];
  currentRecommendations: AIRecommendation[];
  deletedAchievementIds: string[];
  deletedImprovementIds: string[];
  deletedRecommendationIds: string[];
  baselineOperatorNotes: string;
  currentOperatorNotes: string;
};

const isPersistedId = (id?: string): id is string =>
  typeof id === "string" && !id.startsWith("temp-");

const hasAchievementChanged = (a: Achievement, b: Achievement) =>
  a.title !== b.title ||
  a.metric !== b.metric ||
  a.current_value !== b.current_value ||
  (a.comparison ?? "") !== (b.comparison ?? "") ||
  (a.summary ?? "") !== (b.summary ?? "");

const hasImprovementChanged = (a: Improvement, b: Improvement) =>
  a.title !== b.title ||
  a.area !== b.area ||
  a.metric !== b.metric ||
  a.severity !== b.severity ||
  (a.summary ?? "") !== (b.summary ?? "");

const hasRecommendationChanged = (a: AIRecommendation, b: AIRecommendation) =>
  a.title !== b.title ||
  a.description !== b.description ||
  a.impact !== b.impact ||
  JSON.stringify(a.expected_outcomes ?? []) !==
    JSON.stringify(b.expected_outcomes ?? []);

type AchievementAction =
  | {
      id: string;
      title?: string;
      metric?: string;
      current_value?: string;
      comparison?: string;
    }
  | { id: string; delete: true };

type ImprovementAction =
  | {
      id: string;
      title?: string;
      area?: string;
      severity?: string;
      metric?: string;
      summary?: string;
    }
  | { id: string; delete: true };

type RecommendationAction =
  | {
      id: string;
      title: string;
      description: string;
      impact?: "high" | "medium" | "low";
      expected_outcomes?: string[];
    }
  | { id: string; delete: true };

export function buildDraftPatchPayload({
  baselineAchievements,
  baselineImprovements,
  baselineRecommendations,
  currentAchievements,
  currentImprovements,
  currentRecommendations,
  deletedAchievementIds,
  deletedImprovementIds,
  deletedRecommendationIds,
  baselineOperatorNotes,
  currentOperatorNotes,
}: DraftPatchInput): EditDraftRequest | null {
  const baselineAchievementMap = new Map(
    baselineAchievements
      .filter((item) => isPersistedId(item.id))
      .map((item) => [item.id, item] as const),
  );

  const baselineImprovementMap = new Map(
    baselineImprovements
      .filter((item) => isPersistedId(item.id))
      .map((item) => [item.id, item] as const),
  );

  const baselineRecommendationMap = new Map(
    baselineRecommendations
      .filter((item) => isPersistedId(item.id))
      .map((item) => [item.id, item] as const),
  );

  const changedAchievementsById = new Map<string, AchievementAction>();
  const newAchievements: Array<{
    title?: string;
    metric?: string;
    current_value?: string;
    comparison?: string;
  }> = [];

  currentAchievements.forEach((item) => {
    if (isPersistedId(item.id)) {
      const baseline = baselineAchievementMap.get(item.id);
      if (baseline && hasAchievementChanged(item, baseline)) {
        changedAchievementsById.set(item.id, {
          id: item.id,
          title: item.title,
          metric: item.metric,
          current_value: item.current_value,
          ...(item.comparison !== undefined
            ? { comparison: item.comparison }
            : {}),
          ...(item.summary !== undefined ? { summary: item.summary } : {}),
        } as AchievementAction & { id: string; delete?: never });
      }
      return;
    }

    newAchievements.push({
      title: item.title,
      metric: item.metric,
      current_value: item.current_value,
      ...(item.comparison !== undefined ? { comparison: item.comparison } : {}),
      ...(item.summary !== undefined ? { summary: item.summary } : {}),
    });
  });

  const changedImprovementsById = new Map<string, ImprovementAction>();
  const newImprovements: Array<{
    title?: string;
    area?: string;
    severity?: string;
    metric?: string;
    summary?: string;
  }> = [];

  currentImprovements.forEach((item) => {
    if (isPersistedId(item.id)) {
      const baseline = baselineImprovementMap.get(item.id);
      if (baseline && hasImprovementChanged(item, baseline)) {
        changedImprovementsById.set(item.id, {
          id: item.id,
          title: item.title,
          area: item.area,
          severity: item.severity,
          metric: item.metric,
          ...(item.summary !== undefined ? { summary: item.summary } : {}),
        });
      }
      return;
    }

    newImprovements.push({
      title: item.title,
      area: item.area,
      severity: item.severity,
      metric: item.metric,
      ...(item.summary !== undefined ? { summary: item.summary } : {}),
    });
  });

  const changedRecommendationsById = new Map<string, RecommendationAction>();
  const newRecommendations: Array<{
    title: string;
    description: string;
    impact?: "high" | "medium" | "low";
    expected_outcomes?: string[];
  }> = [];

  currentRecommendations.forEach((item) => {
    if (isPersistedId(item.id)) {
      const baseline = baselineRecommendationMap.get(item.id);
      if (baseline && hasRecommendationChanged(item, baseline)) {
        changedRecommendationsById.set(item.id, {
          id: item.id,
          title: item.title,
          description: item.description,
          impact: item.impact,
          ...(item.expected_outcomes !== undefined
            ? { expected_outcomes: item.expected_outcomes }
            : {}),
        });
      }
      return;
    }

    newRecommendations.push({
      title: item.title,
      description: item.description,
      impact: item.impact,
      ...(item.expected_outcomes !== undefined
        ? { expected_outcomes: item.expected_outcomes }
        : {}),
    });
  });

  // Last action wins per ID: apply deletes after updates to override any prior update
  Array.from(new Set(deletedAchievementIds)).forEach((id) => {
    changedAchievementsById.set(id, { id, delete: true });
  });

  Array.from(new Set(deletedImprovementIds)).forEach((id) => {
    changedImprovementsById.set(id, { id, delete: true });
  });

  Array.from(new Set(deletedRecommendationIds)).forEach((id) => {
    changedRecommendationsById.set(id, { id, delete: true });
  });

  const achievementsPayload = [
    ...Array.from(changedAchievementsById.values()),
    ...newAchievements,
  ];

  const improvementsPayload = [
    ...Array.from(changedImprovementsById.values()),
    ...newImprovements,
  ];

  const recommendationsPayload = [
    ...Array.from(changedRecommendationsById.values()),
    ...newRecommendations,
  ];

  const hasNarrativeChange = currentOperatorNotes !== baselineOperatorNotes;

  const payload: EditDraftRequest = {
    ai_insights: {
      ...(hasNarrativeChange
        ? { narrative: { text: currentOperatorNotes } }
        : {}),
      ...(recommendationsPayload.length
        ? { recommendations: recommendationsPayload }
        : {}),
      ...(achievementsPayload.length || improvementsPayload.length
        ? {
            performance_overview: {
              ...(achievementsPayload.length
                ? { achievements: achievementsPayload }
                : {}),
              ...(improvementsPayload.length
                ? { improvements: improvementsPayload }
                : {}),
            },
          }
        : {}),
    },
  };

  if (!payload.ai_insights || Object.keys(payload.ai_insights).length === 0) {
    return null;
  }

  return payload;
}
